import { useEffect, useRef, useState, type DependencyList } from "react";
import { useNotification } from "@/context/NotificationContext";

type FonteOpcao<T> = { buscar: () => Promise<T[] | null | undefined>; mensagemErro: string };

// Carrega N listas de cadastro (loja, setor, cargo, situação...) em paralelo,
// cada uma independente das outras — um Promise.all falhava por inteiro
// quando qualquer uma rejeitava, deixando até as que tiveram sucesso vazias.
// As mensagens de erro de todas as fontes que falharam no mesmo carregamento
// são combinadas num único aviso: o NotificationContext só guarda uma
// mensagem por vez, então notificar uma de cada vez faria a última
// sobrescrever as anteriores.
export function useOpcoesCadastro<T extends Record<string, unknown[]>>(
  fontes: { [K in keyof T]: FonteOpcao<T[K][number]> },
  deps: DependencyList,
): { opcoes: T; carregando: boolean } {
  const { notify } = useNotification();
  const [opcoes, setOpcoes] = useState<T>(
    () => Object.fromEntries(Object.keys(fontes).map((k) => [k, []])) as unknown as T,
  );
  const [carregando, setCarregando] = useState(true);
  const fontesAtual = useRef(fontes);
  fontesAtual.current = fontes;

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    const chaves = Object.keys(fontesAtual.current) as (keyof T)[];
    Promise.allSettled(chaves.map((k) => fontesAtual.current[k].buscar())).then((resultados) => {
      if (cancelado) return;

      const carregadas: Partial<T> = {};
      const mensagensErro: string[] = [];
      for (let i = 0; i < chaves.length; i++) {
        const chave = chaves[i];
        const r = resultados[i];
        if (chave === undefined || r === undefined) continue;
        if (r.status === "fulfilled") {
          carregadas[chave] = (r.value ?? []) as T[typeof chave];
        } else {
          mensagensErro.push(fontesAtual.current[chave].mensagemErro);
        }
      }

      // setOpcoes só combina dados já resolvidos — nada de efeito colateral
      // (notify) dentro do updater, que o StrictMode roda em dobro.
      setOpcoes((atual) => ({ ...atual, ...carregadas }));
      setCarregando(false);
      if (mensagensErro.length > 0) {
        notify(mensagensErro.join("; "), "error");
      }
    });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { opcoes, carregando };
}
